import React from 'react';
import { Box, IconButton, Menu, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { ReactComponent as DotsIcon } from 'img/icons/dots-horizontal.svg';
import { ReactComponent as DotsVertIcon } from 'img/icons/dots-vertical.svg';
import { CustomTheme } from 'common/ui/interfaces';
import { colors } from 'common/ui/theme/default';

interface IProps {
  menuAlign?: 'left' | 'right';
  iconColor?: 'textSecondary' | 'primary';
  icon?: JSX.Element;
  minWidth?: boolean;
  horizontal?: boolean;
  className?: string;
  tableActionsMode?: boolean;
  disableRipple?: boolean;
  moreActionsBtn?: JSX.Element;
  children: React.ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  menu: {
    '& .MuiMenuItem-root .MuiListItemIcon-root:last-child': {
      marginLeft: 0,
    },
  },
  iconButtonActive: {
    '&::before': {
      display: 'block !important',
    },
  },
  iconButton: {
    position: 'relative',
    padding: '0',
    zIndex: 0,
    '&::before': {
      content: '""',
      display: 'none',
      padding: ({ horizontal, tableActionsMode }: IProps) => {
        return horizontal && tableActionsMode ? '16px' : '12px';
      },
      borderRadius: '4px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: ({ iconColor }: IProps) => {
        return iconColor === 'primary' ? theme.palette.primary.dark : colors.lightGray;
      },
      opacity: ({ iconColor }: IProps) => {
        return iconColor === 'primary' ? 0.16 : 1;
      },
      zIndex: -1,
    },
    '&:hover': {
      backgroundColor: 'inherit',
      '&::before': {
        display: ({ icon }: IProps) => {
          return !icon ? 'block' : 'none';
        },
      },
    },
    color: ({ iconColor }: IProps) => {
      if (iconColor === 'primary') {
        return theme.palette.primary.main;
      }

      return theme.palette.text.secondary;
    },
  },
  iconButtonGutterTop: {
    marginTop: theme.spacing(1),
  },
}));

export default function ActionsMenu(props: IProps): JSX.Element {
  const {
    menuAlign = 'right',
    minWidth = false,
    horizontal = false,
    moreActionsBtn,
    tableActionsMode = false,
    disableRipple = true,
    className,
    children,
    icon,
    onOpen,
    onClose,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const classes = useStyles(props);

  const handleOpenClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);

    if (onOpen) {
      onOpen();
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);

    if (onClose) {
      onClose();
    }
  };

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (open) handleCloseMenu();
  };

  let moreActionsIcon =
    icon ||
    (horizontal ? <DotsIcon width={16} height={16} /> : <DotsVertIcon width={16} height={16} />);

  if (horizontal && tableActionsMode) {
    moreActionsIcon = <DotsIcon width={20} height={20} />;
  }

  const boxProps = tableActionsMode ? {} : { minWidth: minWidth ? 55 : 30 };

  return (
    <Box display="inline-flex" height="fit-content" className={className} {...boxProps}>
      {moreActionsBtn ? (
        <Box onClick={handleOpenClick}>{moreActionsBtn}</Box>
      ) : (
        <Tooltip title="More actions">
          <IconButton
            disableRipple={disableRipple}
            className={classNames(classes.iconButton, { [classes.iconButtonActive]: open })}
            aria-label="more"
            aria-controls="actions-menu"
            aria-haspopup="true"
            onClick={handleOpenClick}
          >
            {moreActionsIcon}
          </IconButton>
        </Tooltip>
      )}

      {open ? (
        <Menu
          id="actions-menu"
          className={classes.menu}
          keepMounted
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: menuAlign,
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: menuAlign,
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          onClick={handleClickMenu}
        >
          {children}
        </Menu>
      ) : null}
    </Box>
  );
}
